import type { Zero } from "@rocicorp/zero";
import { ZeroProvider } from "@rocicorp/zero/react";
import { type ReactNode, useEffect, useState } from "react";
import { CLIENT_RELOAD_VERSION } from "../../db/clientReloadVersion";
import type { Context } from "../../shared/authorization";
import { schema } from "../../shared/generatedDrizzleSchema";
import { sharedMutators } from "../../shared/mutators";
import { preloadPersonalChats } from "./PersonalChats/preloadPersonalChats";
import { emitErrorToast } from "../lib/errorToast";
import { getTraceparent } from "../lib/otel";
import { zeroSyncUrl } from "../lib/tenantOrigin";
import {
  emitSpan,
  initTracing,
  sessionId,
  trackConnectionState,
  trackMutation,
} from "../lib/tracing";
import { zeroLogger } from "../lib/zeroLogger";
import { classifyZeroResult, type ZeroResult } from "../lib/zeroResultError";
import { reportZeroDefinitionNotFound } from "../lib/reportZeroDefinitionNotFound";

type User = { id: string; tenantId: string; tenantOrigin: string };

function initZero(z: Zero) {
  patchMutate(z);
  trackConnectionState(z);
  signInAgainWhenAuthLapses(z);
  preloadPersonalChats(z);
}

function signInAgainWhenAuthLapses(z: Zero) {
  let reloaded = false;
  z.connection.state.subscribe((state) => {
    if (state.name !== "needs-auth" || reloaded) return;
    // needs-auth is terminal until connect() is called; reload through the
    // authoritative session bootstrap rather than leaving the tab frozen.
    reloaded = true;
    window.location.reload();
  });
}

function patchMutate(z: Zero) {
  const origMutate = z.mutate;
  const handleError = (r: ZeroResult, scope: string) => {
    if (scope === "server") reportZeroDefinitionNotFound(r.error);
    const action = classifyZeroResult(r);
    if (action.kind === "ignore") return;
    if (action.message === undefined && scope !== "server") return;
    emitErrorToast({
      icon: "circle-off",
      message: action.message ?? "Something went wrong. Please try again.",
      buttonLabel: "Dismiss",
    });
  };
  (z as unknown as Record<string, unknown>).mutate = Object.assign(
    // oxlint-disable-next-line typescript/no-explicit-any -- patching Zero's callable mutate at init
    (mr: any) => {
      const start = performance.now();
      const name: string = mr?.mutator?.mutatorName ?? "unknown";
      const result = origMutate(mr);
      result.client.then((r: ZeroResult) => {
        handleError(r, "client");
        trackMutation(name, start, "client", r);
      });
      result.server.then((r: ZeroResult) => {
        handleError(r, "server");
        trackMutation(name, start, "server", r);
      });
      return result;
    },
    origMutate
  );
}

export function ZeroSetup({
  user,
  children,
}: {
  user: User;
  children: ReactNode;
}) {
  const [zeroBootStartOffset] = useState(() => performance.now());
  const context: Context = { userId: user.id, tenantId: user.tenantId };

  // The internal view syncer cannot infer the tenant from its request host;
  // the API still authorizes this selector against the session cookie.
  const zeroHeaders = {
    "x-session-id": sessionId,
    "x-tenant-id": user.tenantId,
    "x-client-reload-version": String(CLIENT_RELOAD_VERSION),
  };

  useEffect(() => {
    initTracing(user.tenantId);
    emitSpan(
      "ui.zero.boot",
      {
        "zero.boot.start_offset_ms": Math.round(zeroBootStartOffset),
      },
      undefined,
      performance.timeOrigin + zeroBootStartOffset
    );
  }, [user.tenantId, zeroBootStartOffset]);

  return (
    <ZeroProvider
      userID={user.id}
      cacheURL={zeroSyncUrl(user.tenantOrigin)}
      kvStore="mem"
      schema={schema}
      context={context}
      mutators={sharedMutators}
      init={initZero}
      queryHeaders={zeroHeaders}
      mutateHeaders={zeroHeaders}
      getTraceparent={getTraceparent}
      {...zeroLogger}
    >
      {children}
    </ZeroProvider>
  );
}
